import React, { useMemo, useState } from 'react';
import { Button, Modal, Space, Spin, Table, Typography, message } from 'antd';
import { PrinterOutlined, FileTextOutlined } from '@ant-design/icons';
import { getAmortizacionesByCredito } from '../../../../apis/CreditoInfonavitApi';

const { Text, Title } = Typography;

const formatMoney = (value) => {
  const amount = Number(value || 0);
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('es-MX');
};

const getPeriodo = (item) => {
  if (item?.num_periodo && item?.anio) {
    return `${item.num_periodo}/${item.anio}`;
  }
  if (item?.fecha_amortizacion) {
    const date = new Date(item.fecha_amortizacion);
    if (!Number.isNaN(date.getTime())) {
      const mes = String(date.getMonth() + 1).padStart(2, '0');
      return `${mes}/${date.getFullYear()}`;
    }
  }
  return '-';
};

const EstadoCuentaModal = ({ credito, empleadoNombre }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [descuentos, setDescuentos] = useState([]);

  const montoInicial = Number(credito?.monto_inicial || 0);

  const descuentosOrdenados = useMemo(() => {
    return [...descuentos].sort((a, b) => {
      const fechaA = new Date(a.fecha_amortizacion || 0).getTime();
      const fechaB = new Date(b.fecha_amortizacion || 0).getTime();
      return fechaA - fechaB;
    });
  }, [descuentos]);

  const totalDescontado = useMemo(() => {
    return descuentosOrdenados.reduce((total, item) => total + Number(item?.monto || 0), 0);
  }, [descuentosOrdenados]);

  const tableData = useMemo(() => {
    let saldoRestante = montoInicial;

    return descuentosOrdenados.map((item, index) => {
      const saldoAnterior = saldoRestante;
      const monto = Number(item?.monto || 0);
      saldoRestante = Math.max(0, saldoAnterior - monto);

      return {
      key: item.id || `desc-${index}`,
      periodo: getPeriodo(item),
      fecha: formatDate(item.fecha_amortizacion),
      monto,
      referencia: item?.referencia || '-',
      saldoAnterior,
      saldoNuevo: saldoRestante,
    };
    });
  }, [descuentosOrdenados, montoInicial]);

  const montoActual = tableData.length > 0 ? Number(tableData[tableData.length - 1].saldoNuevo || 0) : montoInicial;

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await getAmortizacionesByCredito(credito.id);
      if (response.ok) {
        setDescuentos(response.payload || []);
      } else {
        message.error('No fue posible cargar el estado de cuenta');
      }
    } catch (error) {
      message.error('Error al cargar el estado de cuenta');
    } finally {
      setLoading(false);
    }
  };

  const onOpen = async () => {
    setOpen(true);
    await loadData();
  };

  const printStatement = () => {
    const printWindow = window.open('', '_blank', 'width=1000,height=700');
    if (!printWindow) {
      message.error('No se pudo abrir la ventana de impresión');
      return;
    }

    const rowsHtml = tableData.length
      ? tableData
          .map(
            (row) => `
              <tr>
                <td>${row.periodo}</td>
                <td>${row.fecha}</td>
                <td>${row.referencia}</td>
                <td style="text-align:right;">${formatMoney(row.monto)}</td>
              </tr>
            `
          )
          .join('')
      : '<tr><td colspan="4" style="text-align:center;">No hay descuentos registrados</td></tr>';

    const html = `
      <html>
        <head>
          <title>Estado de Cuenta Infonavit</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #111; }
            h1 { margin: 0 0 8px 0; font-size: 20px; }
            .meta { margin-bottom: 16px; font-size: 14px; }
            .summary { margin: 16px 0; display: flex; gap: 24px; font-size: 14px; }
            table { width: 100%; border-collapse: collapse; margin-top: 16px; }
            th, td { border: 1px solid #ddd; padding: 8px; font-size: 13px; }
            th { background: #f5f5f5; text-align: left; }
            .totals { margin-top: 20px; font-size: 14px; }
          </style>
        </head>
        <body>
          <h1>Estado de Cuenta - Crédito Infonavit</h1>
          <div class="meta">
            <div><strong>Empleado:</strong> ${empleadoNombre || '-'}</div>
            <div><strong>Número de crédito:</strong> ${credito?.numero_credito || '-'}</div>
          </div>
          <div class="summary">
            <div><strong>Monto inicial:</strong> ${formatMoney(montoInicial)}</div>
            <div><strong>Total descontado:</strong> ${formatMoney(totalDescontado)}</div>
            <div><strong>Monto actual:</strong> ${formatMoney(montoActual)}</div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Periodo</th>
                <th>Fecha</th>
                <th>Referencia</th>
                <th>Monto descontado</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const columns = [
    {
      title: 'Periodo',
      dataIndex: 'periodo',
      key: 'periodo',
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
    },
    {
      title: 'Referencia',
      dataIndex: 'referencia',
      key: 'referencia',
    },
    {
      title: 'Monto descontado',
      dataIndex: 'monto',
      key: 'monto',
      align: 'right',
      render: (value) => formatMoney(value),
    },
  ];

  return (
    <>
      <Button icon={<FileTextOutlined />} size="small" onClick={onOpen}>
        Estado de cuenta
      </Button>

      <Modal
        title="Estado de cuenta - Crédito Infonavit"
        open={open}
        onCancel={() => setOpen(false)}
        width={900}
        footer={[
          <Button key="close" onClick={() => setOpen(false)}>
            Cerrar
          </Button>,
          <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={printStatement}>
            Imprimir
          </Button>,
        ]}
      >
        {loading ? (
          <Spin />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            <Title level={5} style={{ margin: 0 }}>
              {empleadoNombre || '-'}
            </Title>
            <Text>Número de crédito: {credito?.numero_credito || '-'}</Text>
            <Text>Monto inicial: {formatMoney(montoInicial)}</Text>
            <Text>Total descontado: {formatMoney(totalDescontado)}</Text>
            <Text strong>Monto actual: {formatMoney(montoActual)}</Text>

            <Table
              size="small"
              columns={columns}
              dataSource={tableData}
              pagination={false}
              locale={{ emptyText: 'No hay descuentos registrados' }}
            />
          </Space>
        )}
      </Modal>
    </>
  );
};

export default EstadoCuentaModal;
